import {
  type DocumentData,
  type DocumentReference,
  Timestamp,
} from 'firebase/firestore';

import { EStatus, type IMetadata, type IObjectId } from './common.type';
import { type ISPhone } from './phone.type';

export const CTenantsPath = 'tenants';

export enum ETier {
  Basic = 'Basic',
  Premium = 'Premium',
  Standard = 'Standard',
}

export type TTier = keyof typeof ETier;

export enum EPaymentMethods {
  CREDIT_CARD = 'Credit Card',
  PAYPAL = 'PayPal',
  BANK_TRANSFER = 'Bank Transfer',
}

export interface ITenantLimits {
  monthlyRequests: number;
  maxSubscriptions: number;
  allowedPaymentMethods: EPaymentMethods[];
}

interface IKYBData {
  tier?: TTier;
  status: EStatus;
  phone?: ISPhone['numberDetails'];
  companyName?: string;
  taxId?: string;
  address?: string;
  documents?: string[]; // IDs uploaded documents
  metadata: IMetadata;
}

type TApiKeyMetadata = {
  expiresAt: Timestamp | null;
  lastUsed: Timestamp | null;
};

export interface IApiKeyPeriodUsage {
  periodStart: Timestamp;
  count: number;
}

export interface IApiKey {
  id: string;
  key: string;
  tenantRef: DocumentReference<DocumentData, DocumentData>;
  usageCount: number;
  periodUsage?: IApiKeyPeriodUsage;
  isActive: boolean;
  permissions: string[];
  metadata: IMetadata & TApiKeyMetadata;
}

export interface ITenant {
  id: IObjectId['id'];
  name: string;
  email: string;
  company?: string;
  kyb?: IKYBData;
  allowedOrigins?: string[];
  subscriptionRefs?: DocumentReference<DocumentData, DocumentData>[];
  metadata: IMetadata;
  status: EStatus;
  limits: ITenantLimits;
}

export const DEFAULT_TENANT: Partial<ITenant> = {
  name: '',
  email: '',
  company: '',
  kyb: {
    tier: 'Basic',
    phone: { country: 'US', nationalNumber: '' },
    status: EStatus.PROCESSING,
    metadata: {
      createdAt: Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date()),
    },
  },
  limits: {
    monthlyRequests: 1000,
    maxSubscriptions: 3,
    allowedPaymentMethods: [],
  },
  allowedOrigins: [],
  subscriptionRefs: [],
};

export const DEFAULT_API_KEY: Partial<IApiKey> = {
  isActive: false,
  metadata: {
    expiresAt: null,
    lastUsed: null,
    createdAt: Timestamp.fromDate(new Date()),
    updatedAt: Timestamp.fromDate(new Date()),
  },
};
